/**
 * macroDisplayCopy — 5-part copy config for the macro text hierarchy.
 * Source of truth for mood labels, subtitles, explanations, takeaways, and footer lines.
 */

export type MacroDisplayState =
  | "DEEP_VALUE"
  | "ACCUMULATION"
  | "NEUTRAL"
  | "WARM"
  | "HOT"
  | "MIXED";

export type MacroDisplayCopy = {
  moodLabel: string;
  plainSubtitle: string;
  explanation: string;
  whatItMeans: string;
  footerLines: string[];
  maxiPhrase?: string;
};

export const MACRO_DISPLAY_COPY: Record<MacroDisplayState, MacroDisplayCopy> = {
  DEEP_VALUE: {
    moodLabel: "Quiet value",
    plainSubtitle: "Looks cheap long term",
    explanation: "Bitcoin is near long-term support and the market is calm.",
    whatItMeans: "Historically this has often been a better area for patient buying than panic.",
    footerLines: [
      "Stay humble.", "Cheap sats.", "Still here.", "Bitcoin does not care.",
      "Quiet conviction.", "Time does the work.", "Stack gently.",
      "Low vibes. High signal.", "Bears are temporary.",
      "Strong hands get silence first.", "Proof of work. Proof of patience.",
      "Cold is where conviction lives.", "Price down. Thesis intact.",
      "Nobody likes it. Good.", "This is where believers are made.",
    ],
    maxiPhrase: "Stay humble.",
  },

  ACCUMULATION: {
    moodLabel: "Thawing",
    plainSubtitle: "Improving, still calm",
    explanation: "Bitcoin is recovering from long-term support and conditions are improving.",
    whatItMeans: "This can be a better phase for gradual positioning than emotional chasing.",
    footerLines: [
      "Tick tock.", "Build quietly.", "Early is boring.", "The thaw begins.",
      "Patience pays first.", "Before it gets crowded.", "Stack before the speeches.",
      "The chain is waking up.", "Calm accumulation hours.",
      "Still cheap enough to think.", "Conviction likes quiet weather.",
      "This is not the exciting part.", "Good. Let them sleep.",
      "Slow is fine.", "No rush. Just sats.",
    ],
    maxiPhrase: "Tick tock.",
  },

  NEUTRAL: {
    moodLabel: "Wait and watch",
    plainSubtitle: "No strong signal",
    explanation: "Bitcoin does not look especially cheap or especially hot right now.",
    whatItMeans: "This is usually a phase where it helps to stay patient and avoid forcing decisions.",
    footerLines: [
      "No strong edge.", "Wait and watch.", "Bitcoin is not in a hurry.",
      "Let the market speak.", "Observe first.", "No rush.",
      "Flat is still information.", "Patience is a position.",
      "Not every week is a gift.", "Signals need time.",
      "No need to force it.", "The chain remains operational.",
      "This is called uncertainty.", "Quiet tape. Open eyes.",
      "Not bearish. Not eager.",
    ],
    maxiPhrase: "Bitcoin does not care.",
  },

  WARM: {
    moodLabel: "Getting crowded",
    plainSubtitle: "Strong, but less comfortable",
    explanation: "Bitcoin is above its long-term trend and market activity is picking up.",
    whatItMeans: "Fresh buying is usually less attractive here than in colder phases.",
    footerLines: [
      "It's getting crowded.", "Discipline matters.", "Don't outsource conviction.",
      "Strong hands, clear head.", "Momentum is not a thesis.",
      "Better late than emotional.", "Crowds are not a strategy.",
      "Good market. Worse entry.", "Excitement is getting expensive.",
      "Still Bitcoin. Still risky.", "Green candles test discipline too.",
      "Up only is not a plan.", "The crowd has arrived.",
      "Price is louder now.", "Stay cooler than the market.",
    ],
    maxiPhrase: "Strong hands.",
  },

  HOT: {
    moodLabel: "Suspiciously sunny",
    plainSubtitle: "Market looks overheated",
    explanation: "Bitcoin is far above its long-term trend and interest is running hot.",
    whatItMeans: "In this kind of environment, discipline usually matters more than excitement.",
    footerLines: [
      "Suspiciously sunny.", "This seems sustainable.", "Euphoria is not a thesis.",
      "Top vibes.", "Too easy is information.", "This is where discipline earns.",
      "Everyone is a genius again.", "Hot money. Cold truth.",
      "Don't confuse heat with value.", "The market is feeling itself.",
      "Great vibes. Expensive sats.", "Risk is back in fashion.",
      "The chart looks loud.", "Stay humble, especially now.",
      "This is not where conviction begins.",
    ],
    maxiPhrase: "Stay humble.",
  },

  MIXED: {
    moodLabel: "Mixed weather",
    plainSubtitle: "Signals disagree",
    explanation: "Some signals look strong, but they are not all pointing in the same direction yet.",
    whatItMeans: "This is not a clean setup, so waiting and caution can make sense.",
    footerLines: [
      "Mixed weather.", "Let it resolve.", "Not all signals agree.",
      "Unclear is still a signal.", "Confusion is not conviction.",
      "Wait for cleaner skies.", "The market is talking in riddles.",
      "One step at a time.", "No clean read.", "Maybe later.",
      "Bitcoin does not reward rushing.", "Some green, some fog.",
      "Not enough clarity yet.", "Watch first. Act later.",
      "Let the tape settle.",
    ],
    maxiPhrase: "Bitcoin does not hurry.",
  },
};

/** Map fallback macro_mode values to display states */
export const MACRO_MODE_FALLBACK_MAP: Record<string, MacroDisplayState> = {
  DEEP_VALUE: "DEEP_VALUE",
  DEEP_BELOW: "DEEP_VALUE",
  ACCUMULATION: "ACCUMULATION",
  NEAR_200WMA: "ACCUMULATION",
  NEUTRAL: "NEUTRAL",
  NORMAL: "NEUTRAL",
  ABOVE: "NEUTRAL",
  WARM: "WARM",
  HOT: "HOT",
  FAR_ABOVE: "HOT",
  MIXED: "MIXED",
};

/** Resolve display macro state from macroAdvisor label or macro_mode fallback */
export function resolveDisplayMacroState(
  macroLabel?: string | null,
  macroMode?: string | null,
): MacroDisplayState {
  if (macroLabel && macroLabel in MACRO_DISPLAY_COPY) {
    return macroLabel as MacroDisplayState;
  }
  if (macroLabel && macroLabel in MACRO_MODE_FALLBACK_MAP) {
    return MACRO_MODE_FALLBACK_MAP[macroLabel];
  }
  if (macroMode && macroMode in MACRO_MODE_FALLBACK_MAP) {
    return MACRO_MODE_FALLBACK_MAP[macroMode];
  }
  return "NEUTRAL";
}

/** Select a stable daily footer line — rotates at most once per day */
export function selectDailyFooterLine(
  footerLines: string[],
  dateStr?: string,
): string {
  if (footerLines.length === 0) return "";
  const d = dateStr ?? new Date().toISOString().slice(0, 10);
  let hash = 0;
  for (let i = 0; i < d.length; i++) {
    hash = (hash * 31 + d.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % footerLines.length;
  return footerLines[index];
}
